import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Slider } from "../components/ui/slider";
import { Textarea } from "../components/ui/textarea";
import { Progress } from "../components/ui/progress";
import { API, errMsg } from "../lib/api";
import { DIMENSIONS, ROLES, MANAGERS, ROLE_NOTE, nearestAnchor } from "../lib/bopta";
import { INDUSTRIES, pickCase, markSeen, caseById } from "../lib/cases";
import { DetailsForm, emptyDetails, detailsValid, phoneOf } from "../components/DetailsForm";
import { rememberResult } from "../lib/device";

export const DRAFT_KEY = "mpwrd_draft_v2";

const stepsFor = (mode) => ["context", ...DIMENSIONS.map((d) => d.k), ...(mode === "manager" ? [] : ["bm"]), "review"];
const LABEL = { context: "Context", bm: "Broken Machine", review: "Review", ...Object.fromEntries(DIMENSIONS.map((d) => [d.k, d.name])) };
const empty = () => ({ role: "", manager: "", answers: Object.fromEntries(DIMENSIONS.map((d) => [d.k, [null, null]])), horizon: Object.fromEntries(DIMENSIONS.map((d) => [d.k, null])), bm: [null, null, null, null], notes: {}, working: null, industry: "general", caseId: pickCase("general") });
const load = () => {
  try { return { ...empty(), ...(JSON.parse(localStorage.getItem(DRAFT_KEY)) || {}) }; } catch { return empty(); }
};
const stepDone = (id, s, hasTeam) => {
  if (id === "context") return Boolean(s.role && s.manager && (hasTeam || s.working !== null));
  if (id === "bm") return s.bm.every((v) => v !== null);
  if (id === "review") return true;
  return s.answers[id].every((v) => v !== null) && s.horizon[id] !== null;
};
const countAnswered = (s, mode) => {
  const base = Object.values(s.answers).flat().concat(Object.values(s.horizon)).filter((v) => v !== null).length;
  return mode === "manager" ? base : base + s.bm.filter((v) => v !== null).length;
};

const Question = ({ d, q, i, value, onChange, manager, label }) => {
  const v = value ?? 50;
  const near = nearestAnchor(q.anchors, v);
  return (
    <div className="panel p-6" data-testid={`question-${d.k}-${i}`}>
      <p className="font-semibold"><span className="mr-2 muted">{label || `${d.k}${i + 1}`}</span>{manager ? q.mq : q.q}</p>
      <div className="mt-5 flex items-center gap-4">
        <span className="display w-12 text-3xl font-extrabold" style={{ color: value === null ? "#C9CCC8" : d.color }} data-testid={`question-value-${d.k}-${i}`}>{v}</span>
        <Slider className="flex-1" value={[v]} max={100} step={1} onValueChange={([x]) => onChange(x)} data-testid={`question-slider-${d.k}-${i}`} />
      </div>
      <div className="mt-4 grid grid-cols-5 gap-1.5">
        {q.anchors.map((a) => (
          <button key={a.v} type="button" onClick={() => onChange(a.v)} data-testid={`anchor-${d.k}-${i}-${a.v}`}
            className="rounded-lg border px-1 py-2 text-[11px] leading-tight sm:text-xs"
            style={value === a.v ? { background: d.color, color: "#fff", borderColor: d.color } : { borderColor: "var(--line)" }}>
            <b className="block">{a.v}</b><span className="hidden sm:block">{a.t}</span>
          </button>
        ))}
      </div>
      <p className="mt-4 rounded-lg p-3 text-sm" style={{ background: d.tint }}><b>{near.t}.</b> <span className="muted">{near.d}</span></p>
    </div>
  );
};

const WorkingToggle = ({ s, setS }) => (
  <div data-testid="working-question">
    <p className="kicker mb-2">Are you currently working? *</p>
    <div className="flex gap-3">
      {[[true, "Yes"], [false, "No"]].map(([v, l]) => (
        <button key={l} type="button" onClick={() => setS({ ...s, working: v })} data-testid={`working-${l.toLowerCase()}`}
          className="btn min-w-[120px] border text-base"
          style={s.working === v ? { background: "var(--teal)", color: "#fff", borderColor: "var(--teal)" } : { borderColor: "var(--line)", background: "#fff" }}>{l}</button>
      ))}
    </div>
    {s.working === false && <p className="mt-3 rounded-lg bg-[#FFEEE5] p-3 text-sm" data-testid="working-no-note">Answer based on your most recent job.</p>}
  </div>
);

const Context = ({ s, setS, mode, team, hasTeam }) => (
  <div className="panel space-y-6 p-6 sm:p-8">
    {team && <p className="rounded-lg bg-[var(--tint)] p-3 text-sm" data-testid="assessment-team-banner">Contributing anonymously to <b>{team.name}</b>{team.org && ` · ${team.org}`}.</p>}
    {mode === "staff" && !hasTeam && (
      <p className="rounded-lg bg-[var(--tint)] p-3 text-sm" data-testid="assessment-staff-banner">
        <b>Staff lens.</b> Score how independently you operate today. Lead a team too? You can assess yourself under both lenses: this staff lens for your own autonomy, and the <Link to="/assessment?mode=manager" target="_blank" className="font-semibold text-[var(--teal)] underline" data-testid="assessment-manager-lens-link">manager lens</Link> for your team. Use the same email for both to see them side by side.
      </p>
    )}
    {mode === "manager" && (
      <div className="rounded-lg bg-[#FDEFE6] p-3 text-sm" data-testid="assessment-manager-banner">
        <p><b>Manager self-assessment.</b> Score how independently you believe your direct reports operate today.</p>
        <p className="mt-2" data-testid="assessment-both-lenses-note">You can assess yourself under both lenses: the <Link to="/assessment" target="_blank" className="font-semibold text-[var(--teal)] underline" data-testid="assessment-staff-lens-link">staff lens</Link> for your own autonomy, and this manager lens for your team. Use the same email for both to see them side by side.</p>
      </div>
    )}
    {!hasTeam && <WorkingToggle s={s} setS={setS} />}
    <div className="grid gap-5 md:grid-cols-2">
      {[["role", mode === "manager" ? "Role of your direct reports" : "Your current role", ROLES], ["manager", mode === "manager" ? "Your own position" : "Your direct reporting manager", MANAGERS]].map(([k, label, opts]) => (
        <div key={k}>
          <p className="kicker mb-2">{label} *</p>
          <Select value={s[k]} onValueChange={(v) => setS({ ...s, [k]: v })}>
            <SelectTrigger className="h-12 bg-white" data-testid={`select-${k}`}><SelectValue placeholder="Select…" /></SelectTrigger>
            <SelectContent>{opts.map((o) => <SelectItem key={o.v} value={o.v} data-testid={`select-${k}-${o.v}`}>{o.l}</SelectItem>)}</SelectContent>
          </Select>
          {k === "role" && <p className="mt-2 text-xs muted" data-testid="role-note">{ROLE_NOTE}</p>}
        </div>
      ))}
    </div>
    {mode !== "manager" && (
      <div className="max-w-md">
        <p className="kicker mb-2">Your industry</p>
        <Select value={s.industry} onValueChange={(v) => setS({ ...s, industry: v, caseId: pickCase(v), bm: [null, null, null, null] })}>
          <SelectTrigger className="h-12 bg-white" data-testid="select-industry"><SelectValue /></SelectTrigger>
          <SelectContent>{INDUSTRIES.map((o) => <SelectItem key={o.v} value={o.v} data-testid={`select-industry-${o.v}`}>{o.l}</SelectItem>)}</SelectContent>
        </Select>
        <p className="mt-2 text-xs muted">Sets the business case you'll solve at the end. Repeat takers get a fresh case.</p>
      </div>
    )}
    <p className="text-sm muted">Answer for your usual working conditions, not an exceptional day. You'll add your name and email at the end; they're never shown on your share link or any team dashboard.</p>
  </div>
);

const DimensionStep = ({ d, s, setS, manager }) => {
  const set = (i, v) => setS((p) => ({ ...p, answers: { ...p.answers, [d.k]: p.answers[d.k].map((x, j) => (j === i ? v : x)) } }));
  return (
    <div className="space-y-5">
      <p className="muted">{d.lead}</p>
      {d.questions.map((q, i) => <Question key={q.q} d={d} q={q} i={i} value={s.answers[d.k][i]} onChange={(v) => set(i, v)} manager={manager} />)}
      <p className="kicker pt-2">Planning horizon</p>
      <Question d={d} q={d.horizon} i="h" label={`${d.k}·H`} value={s.horizon[d.k]} onChange={(v) => setS((p) => ({ ...p, horizon: { ...p.horizon, [d.k]: v } }))} manager={manager} />
      <div className="panel p-6">
        <p className="kicker">Optional · a real example from the last month</p>
        <Textarea className="mt-3" rows={3} maxLength={500} value={s.notes[d.k] || ""} placeholder="e.g. I waited two days for approval on a RM800 refund…"
          onChange={(e) => setS({ ...s, notes: { ...s.notes, [d.k]: e.target.value } })} data-testid={`note-${d.k}`} />
      </div>
    </div>
  );
};

const BrokenMachine = ({ s, setS }) => {
  const BM_CASE = caseById(s.caseId);
  const pick = (qi, v) => setS((p) => ({ ...p, bm: p.bm.map((x, j) => (j === qi ? v : x)) }));
  return (
    <div className="space-y-5" data-testid="bm-step">
      <div className="panel p-6 sm:p-8">
        <p className="kicker">Case brief · {INDUSTRIES.find((i) => i.v === BM_CASE.industry)?.l}</p>
        <p className="mt-3 leading-relaxed">{BM_CASE.brief}</p>
        <table className="mt-5 w-full text-sm">
          <tbody>
            {BM_CASE.table.map((row, r) => (
              <tr key={row[0]} className={r === 0 ? "font-semibold" : "border-t border-[var(--line)]"}>
                {row.map((c, ci) => <td key={`${row[0]}-${ci}`} className="py-2 pr-3">{c}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
        <p className="mt-4 text-xs muted">There's no timer. Choose the option closest to what you would actually do.</p>
      </div>
      {BM_CASE.questions.map((q, qi) => (
        <div key={q.q} className="panel p-6" data-testid={`bm-question-${qi}`}>
          <p className="font-semibold"><span className="mr-2 muted">{qi + 1}/4</span>{q.q}</p>
          <div className="mt-4 space-y-2">
            {q.options.map(([text], oi) => (
              <button key={text} type="button" onClick={() => pick(qi, oi)} data-testid={`bm-option-${qi}-${oi}`}
                className="block w-full rounded-lg border p-3 text-left text-sm transition-colors"
                style={s.bm[qi] === oi ? { borderColor: "var(--teal)", background: "var(--tint)" } : { borderColor: "var(--line)" }}>
                {text}
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

const Review = ({ s, team }) => (
  <div className="panel p-6 sm:p-8" data-testid="assessment-review">
    <div className="grid gap-3 sm:grid-cols-5">
      {DIMENSIONS.map((d) => (
        <div key={d.k} className="rounded-lg p-4" style={{ background: d.tint }}>
          <span className="chip" style={{ background: d.color }}>{d.k}</span>
          <p className="display mt-2 text-3xl font-extrabold">{Math.round(s.answers[d.k].reduce((a, b) => a + b, 0) / 2)}</p>
          <p className="text-xs muted">{d.name}</p>
        </div>
      ))}
    </div>
    <p className="mt-6 muted">We'll compare your average with your role and reporting line, flag your lowest dimension and save your result to a private link. {team && <b>Your scores join {team.name} anonymously.</b>}</p>
  </div>
);

const useTeam = (code) => {
  const [team, setTeam] = useState(null);
  useEffect(() => {
    if (!code) return;
    API.get(`/teams/${code}`).then((r) => setTeam(r.data)).catch(() => toast.error("Team link not found — continuing as an individual."));
  }, [code]);
  return team;
};

const TITLES = { context: "Where do you sit?", bm: "The Broken Machine", review: "Ready to see your profile?" };
const tabClass = (i, step) => {
  if (i === step) return "bg-[var(--ink)] text-white";
  if (i < step) return "bg-[var(--tint)] text-[var(--teal)]";
  return "bg-[#EFEFEA] muted";
};

// Draft holds only anonymous slider answers and a case id (no PII or tokens).
const useDraft = () => {
  const [s, setS] = useState(load);
  useEffect(() => {
    localStorage.setItem(DRAFT_KEY, JSON.stringify(s));
  }, [s]);
  return [s, setS];
};

const payloadFor = (s, mode, team, det) => ({
  name: det.name.trim(), email: det.email.trim(), phone: phoneOf(det), consent: det.consent, updates_opt_in: det.updates,
  working: team ? true : s.working !== false,
  mode, role: s.role, manager: s.manager, answers: s.answers, notes: s.notes, team_code: team?.code || null, horizon: s.horizon,
  bm: mode === "manager" ? null : s.bm.map((oi, i) => caseById(s.caseId).questions[i].options[oi][1]),
  case_id: s.caseId, industry: s.industry,
});

const useSubmit = (s, mode, team, det) => {
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const r = await API.post("/assessments", payloadFor(s, mode, team, det));
      localStorage.removeItem(DRAFT_KEY);
      if (mode === "staff") markSeen(s.caseId);
      rememberResult(mode, r.data.id);
      nav(`/results/${r.data.id}`);
    } catch (e) {
      toast.error(errMsg(e));
      setBusy(false);
    }
  };
  return { busy, submit };
};

const StepTabs = ({ steps, step, done, go }) => (
  <div className="mt-6 flex flex-wrap gap-1.5">
    {steps.map((sid, i) => (
      <button key={sid} onClick={() => (i < step || (done && i === step + 1)) && go(i)} data-testid={`step-tab-${i}`}
        className={`rounded-full px-3 py-1 text-xs font-semibold ${tabClass(i, step)}`}>{LABEL[sid]}</button>
    ))}
  </div>
);

const NavButtons = ({ step, last, done, go, busy, canSubmit, submit }) => (
  <div className="mt-8 flex justify-between">
    <button className="btn btn-line" disabled={step === 0} onClick={() => go(step - 1)} data-testid="assessment-back"><ArrowLeft className="h-4 w-4" /> Back</button>
    {step < last && <button className="btn btn-main" disabled={!done} onClick={() => go(step + 1)} data-testid="assessment-next">Continue <ArrowRight className="h-4 w-4" /></button>}
    {step === last && (
      <button className="btn btn-main" disabled={busy || !canSubmit} onClick={submit} data-testid="assessment-submit">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} See my profile
      </button>
    )}
  </div>
);

export default function Assessment() {
  const [params] = useSearchParams();
  const mode = params.get("mode") === "manager" ? "manager" : "staff";
  const team = useTeam(params.get("team"));
  const [s, setS] = useDraft();
  const [step, setStep] = useState(0);
  const [det, setDet] = useState(emptyDetails);
  const hasTeam = Boolean(params.get("team"));
  const { busy, submit } = useSubmit(s, mode, team, det);

  const steps = stepsFor(mode);
  const id = steps[step];
  const total = mode === "manager" ? 15 : 19;
  const answered = useMemo(() => countAnswered(s, mode), [s, mode]);
  const done = stepDone(id, s, hasTeam);
  const d = DIMENSIONS.find((x) => x.k === id);
  const go = (n) => { setStep(n); window.scrollTo({ top: 0, behavior: "smooth" }); };

  return (
    <div className="wrap max-w-4xl py-12">
      <p className="kicker">{mode === "manager" ? "Manager lens" : "Staff lens"} · {total} questions</p>
      <h1 className="mt-2 text-4xl font-extrabold">{d ? `${d.name}: ${d.sub}` : TITLES[id]}</h1>
      <StepTabs steps={steps} step={step} done={done} go={go} />
      <Progress value={(answered / total) * 100} className="mt-4 h-1.5" />
      <p className="mt-2 text-xs muted" data-testid="assessment-progress-text">{answered} / {total} answered · draft saved on this device</p>
      <div className="fade mt-8" key={step}>
        {id === "context" && <Context s={s} setS={setS} mode={mode} team={team} hasTeam={hasTeam} />}
        {d && <DimensionStep d={d} s={s} setS={setS} manager={mode === "manager"} />}
        {id === "bm" && <BrokenMachine s={s} setS={setS} />}
        {id === "review" && <><Review s={s} team={team} /><div className="mt-5"><DetailsForm v={det} setV={setDet} /></div></>}
      </div>
      <NavButtons step={step} last={steps.length - 1} done={done} go={go} busy={busy} canSubmit={answered === total && detailsValid(det)} submit={submit} />
      {step === 0 && answered > 0 && <button className="mt-6 text-xs underline muted" onClick={() => setS(empty())} data-testid="assessment-clear">Clear saved answers</button>}
    </div>
  );
}
